
/**
 * Module dependencies.
 */

import { AmbientProps } from 'src/types/ambients';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import AmbientCard from 'src/components/cards/ambient-card';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Filters from 'src/components/inspiration/filters';
import FiltersDisclosuresProvider from 'src/context/filters-disclosures/provider';
import Loading from 'src/components/core/loading';
import React, { ComponentType, Fragment, ReactElement } from 'react';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useAmbients, { InitialData } from 'src/api/app/ambients/use-ambients';

/**
 * `Props` type.
 */

type Props = {
  cardComponent?: ComponentType<AmbientProps>,
  className?: string,
  filterParams?: any,
  initialData: InitialData,
  isKiosk?: boolean,
  subtitle: string,
  title: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isKiosk?: boolean }>`
  padding-bottom: ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding-bottom: ${units(25)};

    ${ifProp('isKiosk', css`
      padding-bottom: ${units(32)};
    `)}
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  background-color: ${color('green400')};
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(10)}) 0 ${units(3)};

  ${media.min('ms')`
    padding: ${theme('dimensions.navbarHeight')}px 0 ${units(6)};
  `}
`;

/**
 * `HeaderGrid` styled component.
 */

const HeaderGrid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. .       title    .       .'
      '. .       subtitle .       .'
      '. filters filters  filters .';
    grid-template-columns: 1fr 2fr 6fr 2fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.Paragraph).attrs({ as: 'h1' })`
  color: ${color('beige100')};
  font-style: italic;
  grid-area: title;
  margin-bottom: ${units(2)};
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Type.H3).attrs({ as: 'h2' })`
  color: ${color('white')};
  grid-area: subtitle;
  margin-bottom: ${units(5)};
  text-align: center;

  ${media.min('ms')`
    margin-bottom: ${units(8)};
  `}
`;

/**
 * `StyledFiltersWrapper` styled component.
 */

const StyledFiltersWrapper = styled.div`
  grid-area: filters;
`;

/**
 * `StyledFilters` styled component.
 */

const StyledFilters = styled(Filters)`
  margin: 0;
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  top: 100px;
  transform: translateX(-50%);
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  position: relative;

  &::before {
    background-color: ${color('green400')};
    content: '';
    height: ${units(20)};
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: -1px;
  }
`;

/**
 * `ContentGrid` styled component.
 */

const ContentGrid = styled.div`
  min-height: ${units(50)};
  position: relative;

  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. empty    .'
      '. gallery  .'
      '. loadMore .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled.ul`
  display: grid;
  grid-area: gallery;
  grid-auto-flow: dense;
  grid-auto-rows: max-content;
  grid-gap: 10px;
  grid-template-columns: repeat(2, 1fr);
  list-style: none;
  margin: 0;
  padding: 0;

  ${media.min('ms')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('lg')`
    grid-gap: ${units(2)};
  `}
`;

/**
 * Export `Card` styled component.
 */

export const Card = styled.li`
  ${media.max('ms')`
    &:nth-child(5n) {
      grid-column-start: span 2;
      grid-row-start: span 13;
    }

    &:nth-child(5n - 1),
    &:nth-child(5n - 3) {
      grid-row-end: span 2;
    }

    &:nth-child(5n - 2),
    &:nth-child(5n - 4) {
      grid-row-end: span 12;
    }
  `}

  ${media.min('ms')`
    &:nth-child(5n),
    &:nth-child(5n - 3) {
      grid-row-end: span 2;
    }

    &:nth-child(5n - 1),
    &:nth-child(5n - 4) {
      grid-row-end: span 10;
    }

    &:nth-child(5n - 2) {
      grid-column-start: span 2;
      grid-row-start: span 11;
    }
  `}
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  grid-area: empty;
  padding-top: ${units(30)};
  text-align: center;
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  grid-area: loadMore;
  padding-top: ${units(13)};
  text-align: center;
`;

/**
 * `ListSection` page.
 */

const ListSection = (props: Props): ReactElement => {
  const {
    cardComponent,
    className,
    filterParams,
    initialData,
    isKiosk,
    subtitle,
    title
  } = props;

  const { translate } = useTranslate();
  const {
    data: ambientsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    refetch
  } = useAmbients({ filterParams, initialData });

  const isUpdating = isFetchingNextPage || isFetching;
  const total = ambientsData?.pages[0]?.total ?? 0;

  return (
    <Section isKiosk={isKiosk}>
      <Header className={className}>
        <Container hasPadding={!isKiosk}>
          <HeaderGrid>
            <Title>
              <FadeInUpAnimation>
                {title}
              </FadeInUpAnimation>
            </Title>

            <Subtitle>
              <FadeInUpAnimation>
                {subtitle}
              </FadeInUpAnimation>
            </Subtitle>

            <StyledFiltersWrapper>
              <FiltersDisclosuresProvider>
                <StyledFilters
                  isUpdating={isUpdating}
                  isUpdatingSucceed={isSuccess}
                  onUpdate={refetch}
                />
              </FiltersDisclosuresProvider>
            </StyledFiltersWrapper>
          </HeaderGrid>
        </Container>
      </Header>

      <Content>
        <StyledLoading active={isLoading || isUpdating} />

        <Container hasPadding={!isKiosk}>
          <ContentGrid>
            {isSuccess && (
              <>
                {!isLoading && !isUpdating && total === 0 && (
                  <Empty>
                    <Type.H6
                      as={'p'}
                      paddingVertical={units(1)}
                    >
                      {translate('inspiration:emptyList')}
                    </Type.H6>
                  </Empty>
                )}

                {total > 0 && (
                  <>
                    <Gallery>
                      {map(ambientsData?.pages, (ambients, index: number) => (
                        <Fragment key={index}>
                          {map(ambients?.data, ambient => {
                            const CardComponent = cardComponent ? cardComponent : AmbientCard;

                            return (
                              <Card key={ambient?.id}>
                                <CardComponent
                                  isKioskCard={isKiosk}
                                  {...ambient}
                                />
                              </Card>
                            );
                          })}
                        </Fragment>
                      ))}
                    </Gallery>

                    {hasNextPage && (
                      <LoadMore>
                        <Button
                          disabled={isUpdating}
                          isLoading={isUpdating}
                          onClick={() => fetchNextPage()}
                        >
                          {translate('common:actions.loadMore')}
                        </Button>
                      </LoadMore>
                    )}
                  </>
                )}
              </>
            )}
          </ContentGrid>
        </Container>
      </Content>
    </Section>
  );
};

/**
 * Export `ListSection` page.
 */

export default ListSection;
